import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  CheckCircle2, 
  XCircle,
  TrendingDown,
  Clock,
  Leaf,
  Shield,
  Zap,
  Feather,
  Droplets,
  Dumbbell,
  IndianRupee,
  Wrench,
  Timer,
  Recycle,
  Cable,
  Truck,
  Thermometer,
  Radio,
  ShieldCheck,
  Package,
  Scissors,
  Settings,
  Layers,
  Wallet
} from "lucide-react";

// All 18 GFRP Benefits from the reference image
const gfrpBenefits = [
  // Left column
  { icon: Shield, title: "Corrosion Resistant", description: "Complete immunity to rust and corrosion" },
  { icon: Dumbbell, title: "Stronger", description: "Higher tensile strength than steel" },
  { icon: Feather, title: "Lighter", description: "Up to 80% lighter than TMT bars" },
  { icon: IndianRupee, title: "Cost Effective", description: "Lower lifecycle costs" },
  { icon: Wrench, title: "Zero Maintenance", description: "No periodic maintenance required" },
  { icon: Timer, title: "Longer Life Span", description: "100+ years design life" },
  { icon: Recycle, title: "Eco Friendly", description: "Sustainable and green solution" },
  { icon: Cable, title: "Dielectric", description: "Non-conductive, electrically safe" },
  { icon: Truck, title: "Easy to Transport", description: "Lightweight for easy logistics" },
  // Right column
  { icon: Droplets, title: "Water Resistant", description: "Impervious to water damage" },
  { icon: Thermometer, title: "Less Thermal Expansion", description: "Stable under temperature changes" },
  { icon: Radio, title: "Radio Transparent", description: "No signal interference" },
  { icon: ShieldCheck, title: "Durable", description: "Long-lasting performance" },
  { icon: TrendingDown, title: "Lower Installation Cost", description: "Reduced labor and equipment" },
  { icon: Scissors, title: "Easy Site Handling & Cutting", description: "Simple on-site processing" },
  { icon: Settings, title: "Faster & Safer Installation", description: "Quick and safe to install" },
  { icon: Layers, title: "Reduced Concrete Coverage", description: "Thinner concrete cover needed" },
  { icon: Wallet, title: "Savings in Overlapping Cost", description: "Reduced overlap requirements" },
];

const comparisonData = [
  { property: "Tensile Strength", gfrp: "1171 MPa", gfrpGood: true, steel: "500 MPa", steelGood: false, note: "GFRP carries ~2.3x higher tensile stress per mm²" },
  { property: "Weight/Meter (8mm)", gfrp: "0.105 kg", gfrpGood: true, steel: "0.395 kg", steelGood: false, note: "GFRP is ~75% lighter" },
  { property: "Bar Length", gfrp: "No limitations", gfrpGood: true, steel: "12 meters", steelGood: false, note: "Continuous lengths reduce lap joints" },
  { property: "Durability (embedded)", gfrp: "100 years", gfrpGood: true, steel: "50 years", steelGood: false, note: "GFRP is non-corrosive" },
  { property: "Corrosion Resistance", gfrp: "Yes", gfrpGood: true, steel: "No", steelGood: false, note: "Eliminates cover-related failures" },
  { property: "Electric Conductivity", gfrp: "No", gfrpGood: true, steel: "Yes", steelGood: false, note: "Suitable for power plants, data centers, MRI rooms" },
  { property: "Thermal Conductivity", gfrp: "No", gfrpGood: true, steel: "Yes", steelGood: false, note: "Reduces thermal bridging" },
  { property: "Shear Strength", gfrp: "170 MPa", gfrpGood: true, steel: "120 MPa", steelGood: false, note: "Higher shear strength" },
  { property: "Bond Strength", gfrp: "14 MPa", gfrpGood: true, steel: "9-10 MPa", steelGood: false, note: "Modified development length" },
  { property: "Modulus of Elasticity", gfrp: "46 GPa", gfrpGood: false, steel: "160-200 GPa", steelGood: true, note: "Deflection governs design, not failure" },
  { property: "Density", gfrp: "2.1 ton/m³", gfrpGood: true, steel: "7.8 ton/m³", steelGood: false, note: "Improves seismic performance" },
];

const sustainabilityPoints = [
  "Contributes to LEED certification points",
  "Lower embodied carbon than steel production",
  "Eliminates chemical corrosion treatments",
  "Reduces construction waste",
  "Longer service life = fewer replacements",
  "Non-toxic and environmentally inert"
];

export default function WhyGFRPPage() {
  return (
    <div data-testid="why-gfrp-page">
      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200" data-testid="why-hero">
        <div className="section-container py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="accent-line mb-6"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 font-['Barlow_Condensed']">
              Why Choose GFRP Over Steel?
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl">
              A fact-based comparison of Glass Fiber Reinforced Polymer versus 
              traditional steel reinforcement for modern infrastructure.
            </p>
          </div>
        </div>
      </section>

      {/* GFRP Benefits Grid - Based on reference image */}
      <section className="section-spacing bg-yellow-400" data-testid="gfrp-benefits">
        <div className="section-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Barlow_Condensed']">
              Benefits of GFRP
            </h2>
            <p className="text-yellow-100 mt-4 max-w-2xl mx-auto">
              18 reasons why GFRP outperforms traditional steel reinforcement
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-6">
            {gfrpBenefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg p-4 lg:p-6 text-center shadow-sm hover:shadow-md transition-shadow"
                data-testid={`gfrp-benefit-${index}`}
              >
                <div className="w-14 h-14 bg-white border-2 border-slate-200 rounded-full flex items-center justify-center mx-auto mb-3">
                  <benefit.icon className="w-7 h-7 text-slate-800" />
                </div>
                <h3 className="font-bold text-slate-900 font-['Barlow_Condensed'] text-sm lg:text-base leading-tight">
                  {benefit.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-white" data-testid="comparison-section">
        <div className="section-container">
          <div className="text-center mb-12">
            <div className="accent-line mx-auto mb-6"></div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed']">
              GFRP vs TMT Steel: Head-to-Head
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Technical comparison of material properties and performance characteristics
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" data-testid="comparison-table">
              <thead>
                <tr>
                  <th className="bg-slate-900 text-white p-4 text-left font-['Barlow_Condensed'] text-lg">
                    Property
                  </th>
                  <th className="bg-yellow-400 text-white p-4 text-left font-['Barlow_Condensed'] text-lg">
                    GFRP (PRO BAR)
                  </th>
                  <th className="bg-slate-700 text-white p-4 text-left font-['Barlow_Condensed'] text-lg">
                    TMT Steel
                  </th>
                  <th className="bg-slate-800 text-white p-4 text-left font-['Barlow_Condensed'] text-lg">
                    Key Difference
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, index) => (
                  <tr key={index} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="p-4 font-medium text-slate-900">{row.property}</td>
                    <td className="p-4 bg-yellow-50">
                      <span className="flex items-center gap-2">
                        {row.gfrpGood ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        )}
                        <span className="text-slate-800 font-medium">{row.gfrp}</span>
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="flex items-center gap-2">
                        {row.steelGood ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        )}
                        <span className="text-slate-600">{row.steel}</span>
                      </span>
                    </td>
                    <td className="p-4 text-slate-600 text-sm">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Sustainability Section */}
      <section className="section-spacing bg-slate-50" data-testid="sustainability-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <span className="text-sm font-mono text-yellow-500 uppercase tracking-wider">
                Green Building
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mt-2 mb-6">
                Sustainability & LEED Alignment
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                GFRP contributes to sustainable construction practices and can help projects 
                achieve green building certifications. The material's longevity and 
                maintenance-free nature significantly reduce environmental impact over time.
              </p>
              
              <ul className="space-y-3">
                {sustainabilityPoints.map((point, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white border border-slate-200 p-8 lg:p-12">
              <Leaf className="w-16 h-16 text-yellow-400 mb-6" />
              <h3 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-4">
                Environmental Benefits
              </h3>
              <div className="space-y-4">
                <div className="border-b border-slate-200 pb-4">
                  <p className="font-bold text-slate-900">Reduced Carbon Footprint</p>
                  <p className="text-slate-600 text-sm">Lower embodied energy in production</p>
                </div>
                <div className="border-b border-slate-200 pb-4">
                  <p className="font-bold text-slate-900">No Chemical Treatments</p>
                  <p className="text-slate-600 text-sm">Eliminates anti-corrosion coatings</p>
                </div>
                <div className="border-b border-slate-200 pb-4">
                  <p className="font-bold text-slate-900">Extended Service Life</p>
                  <p className="text-slate-600 text-sm">100+ years reduces replacements</p>
                </div>
                <div>
                  <p className="font-bold text-slate-900">LEED Credits</p>
                  <p className="text-slate-600 text-sm">Contributes to certification goals</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Applications Link Section */}
      <section className="section-spacing bg-slate-900 relative noise-overlay" data-testid="applications-link">
        <div className="section-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="accent-line mb-6"></div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white font-['Barlow_Condensed'] mb-6">
                See GFRP in Action
              </h2>
              <p className="text-slate-400 mb-6 leading-relaxed">
                Now that you know why GFRP outperforms steel, discover where it's being used. 
                From coastal infrastructure to hospital MRI rooms, GFRP is solving real-world 
                challenges across 24+ application areas.
              </p>
              <Link to="/applications">
                <Button 
                  className="bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none px-8 py-6"
                  data-testid="why-applications-btn"
                >
                  Explore Applications
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {["Corrosion-Free Coastal", "Non-Conductive MRI", "Lightweight Transport", "Chemical Resistant"].map((feature, i) => (
                <div key={i} className="bg-slate-800 border border-slate-700 p-6 text-center hover:border-yellow-400 transition-colors">
                  <p className="text-white font-bold font-['Barlow_Condensed']">{feature}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing bg-yellow-400" data-testid="why-cta">
        <div className="section-container text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-6">
            Ready to Make the Switch?
          </h2>
          <p className="text-slate-700 max-w-2xl mx-auto mb-10">
            Get a customized cost analysis and technical consultation for your 
            project from our engineering team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                className="bg-slate-900 text-white hover:bg-slate-800 font-bold uppercase tracking-wider rounded-none px-8 py-6"
                data-testid="why-contact-btn"
              >
                Get Project Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/applications">
              <Button 
                variant="outline"
                className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold uppercase tracking-wider rounded-none px-8 py-6"
                data-testid="why-applications-btn-2"
              >
                View Applications
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
